import React, {useEffect, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {tomorrow} from 'react-syntax-highlighter/dist/esm/styles/prism';
import {API_BASE_URL, publicAPI} from '../services/api';
import tcgDataService from '../services/tcgDataService';

const TcgDocumentation = () => {
  const { tcg } = useParams();
  const [tcgData, setTcgData] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiExamples, setApiExamples] = useState(null);
  const [expandedExamples, setExpandedExamples] = useState({});
  const [lastUpdatedData, setLastUpdatedData] = useState(null);

  useEffect(() => {
    const fetchTcgData = async () => {
      try {
        // Fetch endpoints, examples, and last updated data in parallel
          const [endpointsResult, examplesResult, lastUpdatedResponse] = await Promise.all([
              tcgDataService.getDocEndpoints(),
              tcgDataService.getDocExamples(),
          publicAPI.getLastUpdated(tcg.toLowerCase()).catch(() => null)
        ]);

          if (endpointsResult) {
              const allEndpoints = endpointsResult.endpoints;
              const supportedTcgs = endpointsResult.supportedTcgs;

          // Find the specific TCG
          const targetTcg = supportedTcgs.find(t => t.name.toLowerCase() === tcg.toLowerCase());
          if (!targetTcg) {
            setError(`TCG "${tcg}" not found`);
            return;
          }

          setTcgData(targetTcg);

          // Find TCG-specific endpoints
          const tcgEndpoints = allEndpoints.filter(category =>
            category.tcg && category.tcg.toLowerCase() === tcg.toLowerCase()
          );

          // Also include general TCG endpoints
          const generalEndpoints = allEndpoints.find(category =>
            category.category === 'TCG General'
          );

          if (generalEndpoints) {
            setEndpoints([generalEndpoints, ...tcgEndpoints]);
          } else {
            setEndpoints(tcgEndpoints);
          }
        } else {
          setError('Failed to fetch TCG data');
        }

          if (examplesResult && examplesResult.examples) {
              setApiExamples(examplesResult.examples);
        } else {
              console.warn('Failed to fetch API examples');
        }

        // Set last updated data
        if (lastUpdatedResponse && lastUpdatedResponse.data.success) {
          setLastUpdatedData(lastUpdatedResponse.data.data);
        } else {
          console.warn('Failed to fetch last updated data for', tcg);
        }
      } catch (err) {
        console.error('Error fetching TCG data:', err);
        setError('Failed to load TCG documentation');
      } finally {
        setLoading(false);
      }
    };

    fetchTcgData();
  }, [tcg]);

  const toggleExample = (endpointKey) => {
    setExpandedExamples(prev => ({
      ...prev,
      [endpointKey]: !prev[endpointKey]
    }));
  };

  const formatLastUpdated = (dateString) => {
    if (!dateString || dateString === null) return 'Unknown';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Unknown';
      }
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
      });
    } catch (err) {
      return 'Unknown';
    }
  };

  const getExampleData = (endpoint) => {
    if (!apiExamples || !tcgData) {
      // Fallback if examples haven't loaded yet
      return {
        request: `curl -X ${endpoint.method} "https://api.tcg-api.eu${endpoint.path}" \\
  -H "X-API-Key: tcg_your_api_key_here"`,
        response: `{
  "success": true,
  "message": "Loading examples...",
  "data": {}
}`
      };
    }

    const path = endpoint.path;
    const tcgName = tcgData.name.toLowerCase();
    const tcgExamples = apiExamples.tcgs?.[tcgName]?.endpoints;

    if (!tcgExamples) {
      return {
        request: `curl -X ${endpoint.method} "https://api.tcg-api.eu${path}" \\
  -H "X-API-Key: tcg_your_api_key_here"`,
        response: `{
  "success": true,
  "message": "No examples available for this TCG",
  "data": {}
}`
      };
    }

    // Determine which example to use based on the endpoint
    // Order matters - check more specific patterns first
    let exampleKey = null;
    if (path.includes('/sets/{setId}/cards')) {
      exampleKey = 'set_cards';
    } else if (path.includes('/cards/{cardId}')) {
      exampleKey = 'card';
    } else if (path.includes('/sets/{setId}')) {
      exampleKey = 'set';
    } else if (path.includes('/search')) {
      exampleKey = 'search';
    } else if (path.includes('/sets') && !path.includes('{')) {
      exampleKey = 'sets';
    } else if (path.includes('/cards') && !path.includes('{')) {
      exampleKey = 'cards';
    }

    const example = tcgExamples[exampleKey];
    if (example) {
      return {
        request: example.request.curl,
        response: JSON.stringify(example.response, null, 2)
      };
    }

    // Fallback
    return {
      request: `curl -X ${endpoint.method} "https://api.tcg-api.eu${path}" \\
  -H "X-API-Key: tcg_your_api_key_here"`,
      response: `{
  "success": true,
  "message": "Example not found",
  "data": {}
}`
    };
  };

  const generateExamples = () => {
    if (!tcgData) return {};

    const tcgName = tcgData.name.toLowerCase();
    // Use the same API_BASE_URL that's used throughout the app
    const baseUrl = API_BASE_URL.replace('/api/v1', '');

    return {
      getSets: `curl -X GET "${baseUrl}/api/v1/tcg/${tcgName}/sets?limit=20&offset=0" \\
  -H "X-API-Key: tcg_your_api_key_here"`,

      getCards: `curl -X GET "${baseUrl}/api/v1/tcg/${tcgName}/cards?limit=50&offset=0" \\
  -H "X-API-Key: tcg_your_api_key_here"`,

      searchCards: `curl -X GET "${baseUrl}/api/v1/tcg/${tcgName}/search?q=${tcgName === 'pokemon' ? 'pikachu' : 'elsa'}&limit=20&offset=0" \\
  -H "X-API-Key: tcg_your_api_key_here"`,

      javascript: `const apiKey = 'tcg_your_api_key_here';
const baseURL = '${API_BASE_URL}';

// Get ${tcgData.display_name} sets
const sets = await fetch(\`\${baseURL}/tcg/${tcgName}/sets\`, {
  headers: { 'X-API-Key': apiKey }
}).then(res => res.json());

// Search for cards
const cards = await fetch(\`\${baseURL}/tcg/${tcgName}/search?q=${tcgName === 'pokemon' ? 'charizard' : 'elsa'}\`, {
  headers: { 'X-API-Key': apiKey }
}).then(res => res.json());`,

      python: `import requests

api_key = 'tcg_your_api_key_here'
base_url = '${baseUrl}/api/v1'
headers = {'X-API-Key': api_key}

# Get ${tcgData.display_name} sets
response = requests.get(f'{base_url}/tcg/${tcgName}/sets', headers=headers)
sets = response.json()

# Search for cards
params = {'q': '${tcgName === 'pokemon' ? 'charizard' : 'elsa'}', 'limit': 20}
response = requests.get(f'{base_url}/tcg/${tcgName}/search', headers=headers, params=params)
cards = response.json()`
    };
  };

  const renderEndpoint = (endpoint) => {
    const endpointKey = `${endpoint.method}-${endpoint.path}`;
    const isExpanded = expandedExamples[endpointKey];
    const exampleData = getExampleData(endpoint);

    return (
      <div key={endpointKey} className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-3">
            <span className={`px-2 py-1 text-xs font-medium rounded ${
              endpoint.method === 'GET' ? 'bg-green-100 text-green-800' :
              endpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' :
              endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {endpoint.method}
            </span>
            <code className="text-sm font-mono">{endpoint.path}</code>
          </div>
          <button
            onClick={() => toggleExample(endpointKey)}
            className="btn btn-sm btn-outline-primary"
            disabled={!apiExamples}
          >
            <span>{apiExamples ? 'Try Example' : 'Loading examples...'}</span>
            <svg
              className={`w-4 h-4 ml-1 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        <p className="text-gray-600 text-sm mb-2">{endpoint.description}</p>

        {endpoint.pathParams && endpoint.pathParams.length > 0 && (
          <div className="mb-2">
            <p className="text-gray-500 text-xs font-medium mb-1">Path Parameters:</p>
            <div className="space-y-1">
              {endpoint.pathParams.map(param => (
                <div key={param.name} className="text-xs text-gray-500">
                  <code className="bg-gray-100 px-1 rounded">{param.name}</code>: {param.description}
                  {param.example && <span className="ml-1">(e.g., "{param.example}")</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {endpoint.params && endpoint.params.length > 0 && (
          <div className="mb-2">
            <p className="text-gray-500 text-xs font-medium mb-1">Query Parameters:</p>
            <div className="space-y-1">
              {endpoint.params.map(param => (
                <div key={param.name} className="text-xs text-gray-500">
                  <code className="bg-gray-100 px-1 rounded">{param.name}</code>
                  {param.required && <span className="text-red-500 ml-1">*</span>}: {param.description}
                  {param.default !== undefined && <span className="ml-1">(default: {param.default})</span>}
                  {param.example && <span className="ml-1">(e.g., "{param.example}")</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {isExpanded && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.013 8.013 0 01-7-4c0-4.418 3.582-8 8-8s8 3.582 8 8z" />
                  </svg>
                  Example Request
                </h4>
                <SyntaxHighlighter language="bash" style={tomorrow} className="!mt-0 !mb-0">
                  {exampleData.request}
                </SyntaxHighlighter>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Example Response
                </h4>
                <SyntaxHighlighter language="json" style={tomorrow} className="!mt-0 !mb-0">
                  {exampleData.response}
                </SyntaxHighlighter>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading TCG documentation...</p>
        </div>
      </div>
    );
  }

  if (error || !tcgData) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h1 className="text-xl font-bold text-red-700 mb-2">TCG Not Found</h1>
          <p className="text-red-600 mb-4">{error || `TCG "${tcg}" not found`}</p>
          <div className="space-y-2">
            <p className="text-sm text-red-600">Available TCGs:</p>
            <Link to="/docs" className="text-blue-600 hover:text-blue-800 text-sm underline">
              View all available TCGs in the main documentation
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const examples = generateExamples();

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
          <Link to="/docs" className="hover:text-gray-700">Documentation</Link>
          <span>/</span>
          <span className="font-medium">{tcgData.display_name}</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900">{tcgData.display_name} API Documentation</h1>
        <span className="text-gray-400">Last updated: {formatLastUpdated(lastUpdatedData?.last_updated?.cards)}</span>
        <p className="text-gray-600 mt-2">
          Complete API reference for {tcgData.display_name} trading card data.
          {tcgData.description && ` ${tcgData.description}`}
        </p>
      </div>

      {/* Quick Start */}
      <div className="card p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Start</h2>
        <p className="text-gray-600 mb-4">
          Get started with {tcgData.display_name} API endpoints. All requests require an API key.
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">Base URL Pattern</h3>
          <code className="text-sm text-blue-800">
            /api/v1/tcg/{tcgData.name.toLowerCase()}/{'{endpoints}'}
          </code>
        </div>
      </div>

      {/* Authentication */}
      <div className="card p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication</h2>
        <p className="text-gray-600 mb-4">
          All {tcgData.display_name} endpoints require authentication via API key:
        </p>

        <div className="bg-gray-50 rounded-lg p-4">
          <SyntaxHighlighter language="bash" style={tomorrow} className="!mt-0">
            {`curl -H "X-API-Key: tcg_your_api_key_here" https://api.tcg-api.eu/api/v1/tcg/${tcgData.name.toLowerCase()}/sets`}
          </SyntaxHighlighter>
        </div>

        <p className="text-gray-600 text-sm mt-4">
          Don't have an API key? <Link to="/register" className="text-blue-600 hover:text-blue-800">Register for free</Link> to get started.
        </p>
      </div>

      {/* Endpoints */}
      <div className="card p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">{tcgData.display_name} Endpoints</h2>

        {endpoints.length > 0 && (
          <div className="space-y-8">
            {endpoints.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                  {category.category}
                </h3>
                <div className="space-y-4">
                  {category.endpoints.map(renderEndpoint)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Code Examples */}
      <div className="card p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Code Examples</h2>

        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">cURL Examples</h3>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Get {tcgData.display_name} Sets</h4>
                <SyntaxHighlighter language="bash" style={tomorrow}>
                  {examples.getSets}
                </SyntaxHighlighter>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Get {tcgData.display_name} Cards</h4>
                <SyntaxHighlighter language="bash" style={tomorrow}>
                  {examples.getCards}
                </SyntaxHighlighter>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Search {tcgData.display_name} Cards</h4>
                <SyntaxHighlighter language="bash" style={tomorrow}>
                  {examples.searchCards}
                </SyntaxHighlighter>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">JavaScript</h3>
            <SyntaxHighlighter language="javascript" style={tomorrow}>
              {examples.javascript}
            </SyntaxHighlighter>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Python</h3>
            <SyntaxHighlighter language="python" style={tomorrow}>
              {examples.python}
            </SyntaxHighlighter>
          </div>
        </div>
      </div>

      {/* Response Format */}
      <div className="card p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Response Format</h2>
        <p className="text-gray-600 mb-4">All {tcgData.display_name} API responses follow a consistent JSON format:</p>

        <SyntaxHighlighter language="json" style={tomorrow}>
{`{
  "success": true,
  "message": "Request completed successfully",
  "data": {
    // ${tcgData.display_name} data here
  },
  "pagination": {
    "total": 150,
    "limit": 20,
    "offset": 0,
    "hasMore": true
  }
}`}
        </SyntaxHighlighter>
      </div>

      {/* Postman Collection */}
      <div className="card p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Postman Collection</h2>
        <p className="text-gray-600 mb-4">
          Get a ready-to-use Postman collection with all {tcgData.display_name} endpoints:
        </p>
        <div className="flex items-center space-x-4">
          <a
            href={`${API_BASE_URL}/docs/postman`}
            download="tcg-api-postman-collection.json"
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            Download Collection
          </a>
          <a
            href="https://www.postman.com/tdruben/tdr-tcg-api/collection/wcncnt6/tcg-api-public-endpoints"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            View in Postman Web
          </a>
        </div>
        <p className="text-gray-500 text-sm mt-4">
          The collection includes a dedicated folder for {tcgData.display_name} with all endpoints configured.
        </p>
      </div>

      {/* Back to All Docs */}
      <div className="card p-6 bg-gray-50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Need documentation for other TCGs?</h3>
            <p className="text-gray-600 text-sm">View the complete API documentation with all supported TCGs.</p>
          </div>
          <Link
            to="/docs"
            className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            View All Documentation
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TcgDocumentation;